import { useState } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRenterInfoRenterProfileWebApiHandler_getForStandalonePage,
  useRenterInfoRenterProfileWebApiHandler_insertOrUpdateForStandalonePage,
} from '@zg-rentals/ts-renter-info-client-react-query';
import type {
  RenterInfoRenterProfileInsertOrUpdateForStandaloneRequestDto,
  RenterInfoRenterProfileStandalonePageWebApiResponse,
} from '@zg-rentals/ts-renter-info-client';
import { Box, Spinner, Toast, useToast } from '@zillow/constellation-10';
import { cleanPhoneNumber } from '@zg-rentals/renter-profile-components';
import { ViewRenterProfileContent } from './components/ViewRenterProfileContent';
import { EditRenterProfileContent } from './components/EditRenterProfileContent';

type Profile = RenterInfoRenterProfileStandalonePageWebApiResponse;

type CustomErrorFields = 'firstName' | 'lastName' | 'phoneNumber';

export type MapCustomErrorFromRequest = Record<CustomErrorFields, string | undefined>;

const SAVE_ERROR_TOAST = "Couldn't save your renter profile. Please try again.";
const FIRST_NAME_INVALID_CHARACTERS_ERROR_MESSAGE = 'First name can only contain letters.';
const LAST_NAME_INVALID_CHARACTERS_ERROR_MESSAGE = 'Last name can only contain letters.';
const SAVE_NAMES_ERROR_MESSAGE = 'There was an error saving your renter profile, please try again later';

const ANSWER_FIELDS = [
  'creditScore',
  'estimatedHouseholdIncome',
  'isParkingNeeded',
  'pets',
  'preferredBedroomCount',
  'preferredLeaseDuration',
  'preferredMoveInDate',
  'totalPeopleInHousehold',
  'voucherAmountMonthly',
] as const satisfies ReadonlyArray<keyof Profile>;

// True when only the identity fields (name/phone/email) are set and no
// profile answers have been provided yet.
const hasNoAnswers = (profile: Profile): boolean =>
  ANSWER_FIELDS.every((field) => profile[field] === undefined || profile[field] === null);

const convertProfileToInsertOrUpdateRequestDto = (
  profile: Profile,
): RenterInfoRenterProfileInsertOrUpdateForStandaloneRequestDto => ({
  firstName: profile.firstName ?? '',
  lastName: profile.lastName ?? '',
  email: profile.email ?? '',
  phoneNumber: cleanPhoneNumber(profile.phoneNumber ?? ''),
  creditScore: profile.creditScore,
  estimatedHouseholdIncome: profile.estimatedHouseholdIncome,
  isParkingNeeded: profile.isParkingNeeded,
  pets: profile.pets,
  preferredBedroomCount: profile.preferredBedroomCount,
  preferredLeaseDuration: profile.preferredLeaseDuration,
  preferredMoveInDate: profile.preferredMoveInDate,
  totalPeopleInHousehold: profile.totalPeopleInHousehold,
  voucherAmountMonthly: profile.voucherAmountMonthly,
});

export const RenterProfilePage = () => {
  const [isEditingOverride, setIsEditingOverride] = useState<boolean | null>(null);
  const [scrollToTopOnView, setScrollToTopOnView] = useState(false);
  const [customErrorMap, setCustomErrorMap] = useState<MapCustomErrorFromRequest>({
    firstName: undefined,
    lastName: undefined,
    phoneNumber: undefined,
  });

  const queryClient = useQueryClient();
  const { enqueueToast } = useToast();

  const { data, isLoading } = useRenterInfoRenterProfileWebApiHandler_getForStandalonePage({
    meta: {
      tags: ['renter-profile'],
      context: {
        api: 'renter-info',
        details: 'Fetching renter profile for the standalone renter profile page',
      },
    },
  });

  const { mutate: insertOrUpdateForStandalonePageMutate, isPending } =
    useRenterInfoRenterProfileWebApiHandler_insertOrUpdateForStandalonePage({
      meta: {
        context: {
          api: 'renter-info',
          details: 'Updating renter profile from the standalone renter profile page',
        },
      },
      onSuccess: (updatedProfile) => {
        queryClient.setQueryData(useRenterInfoRenterProfileWebApiHandler_getForStandalonePage.getKey(), updatedProfile);
      },
    });

  if (isLoading) {
    return (
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'loose',
          backgroundColor: 'bg.neutral',
          borderRadius: 'obj.default',
          paddingBlock: 'loose',
          paddingInline: 'loose',
          alignItems: 'center',
        }}
      >
        <Spinner title="Loading renter profile" size="xl" />
      </Box>
    );
  }

  const isEditing = isEditingOverride ?? hasNoAnswers(data ?? {});

  if (isEditing) {
    return (
      <EditRenterProfileContent
        profile={data ?? {}}
        isSaving={isPending}
        customErrorMap={customErrorMap}
        setCustomErrorMap={setCustomErrorMap}
        onCancel={() => setIsEditingOverride(false)}
        onSave={(profile) =>
          insertOrUpdateForStandalonePageMutate(
            { postData: convertProfileToInsertOrUpdateRequestDto(profile) },
            {
              onSuccess: () => {
                setIsEditingOverride(false);
                setScrollToTopOnView(true);
              },
              onError: (error) => {
                const errorMessage = error instanceof Error ? error.message : SAVE_ERROR_TOAST;
                if (errorMessage.startsWith('firstName')) {
                  setCustomErrorMap((prev) => ({ ...prev, firstName: FIRST_NAME_INVALID_CHARACTERS_ERROR_MESSAGE }));
                } else if (errorMessage.startsWith('lastName')) {
                  setCustomErrorMap((prev) => ({ ...prev, lastName: LAST_NAME_INVALID_CHARACTERS_ERROR_MESSAGE }));
                } else if (errorMessage.includes(SAVE_NAMES_ERROR_MESSAGE)) {
                  enqueueToast(<Toast tone="critical">{SAVE_NAMES_ERROR_MESSAGE}</Toast>);
                  return;
                }
                enqueueToast(<Toast tone="critical">{SAVE_ERROR_TOAST}</Toast>);
              },
            },
          )
        }
      />
    );
  }

  return (
    <ViewRenterProfileContent
      profile={data ?? {}}
      scrollOnMount={scrollToTopOnView}
      onEdit={() => {
        setIsEditingOverride(true);
        setScrollToTopOnView(false);
      }}
    />
  );
};
